import { RefObject } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNotification, useTranslate } from "@refinedev/core";

import { ModalRef } from "@shared/components";
import { useEditPromocodeMutation } from "@modules/promocodes/services/mutations";
import { PromocodeFormValues, promocodeSchema } from "@modules/promocodes/validations";
import { listPromocodesQueryKey } from "@modules/promocodes/services/queries/listPromocodes/query";
import { EditPromocodeModalProps } from "./types";

export function useEditPromocodeModal({ modalRef, onComplete }: EditPromocodeModalProps) {
  const queryClient = useQueryClient();
  const translate = useTranslate();
  const { open } = useNotification();

  const methods = useForm<PromocodeFormValues>({
    resolver: zodResolver(promocodeSchema()),
    mode: "onBlur",
  });

  const { mutateAsync, isPending } = useEditPromocodeMutation({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: listPromocodesQueryKey,
        refetchType: 'all',
      });

      open?.({
        type: "success",
        message: translate("promocode_admin.success.edit"),
      });

      onComplete();
      modalRef.current?.close();
    },
  });

  const handleSubmit = async (data: PromocodeFormValues) => {
    const promocodeId = modalRef.current?.getId();
    await mutateAsync({ id: promocodeId as string, data });
  }

  return {
    methods,
    isPending,
    handleSubmit,
  };
}
